/**
 * addressBook.js — Local address memory for dispatcher speed.
 *
 * Stores previously used addresses in localStorage so dispatchers can
 * quickly reuse pickup/delivery locations without retyping.
 *
 * Each entry shape:
 *   { address, lat, lng, clientName, facilityName, usedAt }
 *
 * Max 50 entries — oldest entries are dropped when the cap is exceeded.
 * Deduplication is case-insensitive on the address string.
 */

const BOOK_KEY = "ci_address_book";
const MAX_ENTRIES = 50;

/** Read the full address book from localStorage. */
export function getAddressBook() {
  try {
    const raw = localStorage.getItem(BOOK_KEY);
    const parsed = JSON.parse(raw || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Save a new entry to the address book.
 * Deduplicates by address string (case-insensitive) — if the same address
 * was used before it is moved to the top (most recent) instead of duplicated.
 *
 * @param {{ address: string, lat?: number|null, lng?: number|null,
 *            clientName?: string, facilityName?: string }} entry
 */
export function saveToAddressBook({ address, lat, lng, clientName, facilityName } = {}) {
  const trimmed = (address || "").trim();
  if (!trimmed) return;

  const book = getAddressBook().filter(
    (e) => e.address.toLowerCase() !== trimmed.toLowerCase()
  );

  book.unshift({
    address:      trimmed,
    lat:          lat  ?? null,
    lng:          lng  ?? null,
    clientName:   (clientName  || "").trim(),
    facilityName: (facilityName || "").trim(),
    usedAt:       new Date().toISOString(),
  });

  if (book.length > MAX_ENTRIES) book.length = MAX_ENTRIES;

  try {
    localStorage.setItem(BOOK_KEY, JSON.stringify(book));
  } catch {
    // localStorage full — silently skip; address memory is best-effort
  }
}

/**
 * Search the address book for entries matching a partial query string.
 * Matches against address, clientName, and facilityName.
 * Results are already sorted by recency (most recent first).
 *
 * @param {string} query   - Partial text typed by the dispatcher
 * @param {number} [limit] - Max number of results (default 6)
 * @returns {Array<{ address, lat, lng, clientName, facilityName, usedAt }>}
 */
export function searchAddressBook(query, limit = 6) {
  if (!query || query.trim().length < 2) return [];
  const q = query.toLowerCase();
  return getAddressBook()
    .filter(
      (e) =>
        e.address.toLowerCase().includes(q) ||
        (e.clientName   || "").toLowerCase().includes(q) ||
        (e.facilityName || "").toLowerCase().includes(q)
    )
    .slice(0, limit);
}
